import * as React from 'react';
import { getItemSavedSearchQuery, getItemSavedSearchType } from '@/utils/ItemPageUtils';
import Button from '@liveauctioneers/caterwaul-components/lib/Button/Button';
import classNames from 'classnames';
import FollowSellerContainer from '@/components/FollowSeller/FollowSellerContainer';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import { FormattedMessage } from 'react-intl';
import { getBiddingInfo } from '@/redux/modules/biddingInfo';
import { getCatalog } from '@/redux/modules/catalog';
import { getImageUrl } from '@/utils/urls';
import { getItem } from '@/redux/modules/item';
import { getItemFacets } from '@/redux/modules/itemFacets';
import { getSeller } from '@/redux/modules/seller';
import ImageWithFallback from '@liveauctioneers/caterwaul-components/lib/ImageWithFallback/ImageWithFallback2';
import isEqual from 'react-fast-compare';
import ItemPriceGatewayContainer from '@/components/ItemPriceGateway/ItemPriceGatewayContainer';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import { safelyRender } from '@/utils/safelyRender';
import SaveSearchContainer from '@/components/SaveSearch/SaveSearchContainer';
import styles from './SoldSnippet.module.scss';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import { useSelector } from 'react-redux';

type Props = {
    className?: string;
    itemId: number;
    scrollToItem: Function;
};

const SoldSnippet = ({ className, itemId, scrollToItem }: Props) => {
    const { isPassed, salePrice } = useSelector((state) => getBiddingInfo(state, itemId), isEqual);
    const { catalogId, imageVersion, photos = [], sellerId, title } = useSelector(
        (state) => getItem(state, itemId),
        isEqual
    );
    const { currency = 'usd', isCatalogOnly } = useSelector((state) => getCatalog(state, catalogId), isEqual);
    const { name: sellerName } = useSelector((state) => getSeller(state, sellerId), isEqual);
    const facets = useSelector((state) => getItemFacets(state, itemId));

    const imageNumber = photos?.[0] || 0;
    const imageUrl = getImageUrl({
        catalogId,
        imageNumber,
        imageVersion,
        itemId,
        sellerId,
        width: 200,
    });
    const imageUrlPlaceholder = getImageUrl({
        catalogId,
        imageNumber,
        imageVersion,
        itemId,
        quality: 15,
        sellerId,
        width: 200,
    });
    const wrapperClasses = classNames(styles['sold-snippet-wrapper'], className);
    const safeTitle = safelyRender(title, 'span');
    const narrow = useIsNarrowerThan(561);

    const saveSearchParams = getItemSavedSearchQuery(facets);
    // todo: doesn't work, needs transform from ItemFacet > Search facet format
    const allSearchParams = { ...saveSearchParams, facets: [facets] };

    const itemStatusNode = (
        <div className={styles['item-status']}>
            <ItemPriceGatewayContainer className={styles['login-link']} itemId={itemId}>
                {isPassed ? (
                    <span className={styles['item-card-label']}>
                        {isCatalogOnly ? <FormattedMessage id="lot_closed" /> : <FormattedMessage id="lot_passed" />}
                    </span>
                ) : (
                    <span className={styles['item-card-label']}>
                        <FormattedMessage
                            id="sold_for"
                            values={{
                                amount: <FormattedCurrency noFraction sourceCurrency={currency} value={salePrice} />,
                            }}
                        />
                    </span>
                )}
            </ItemPriceGatewayContainer>
        </div>
    );

    const itemDetailsNode = (
        <div className={styles['item-details-wrapper']}>
            <Button className={styles['item-details-link']} onClick={scrollToItem} secondary small>
                <FormattedMessage id="view_details" />
            </Button>
        </div>
    );

    const itemCTANode = (
        <div className={styles['item-cta-wrapper']}>
            <Link className={styles['item-cta-link']} primaryWeak route to={`/consignments/${sellerId}`}>
                <FormattedMessage id="sell_similar_item" />
            </Link>
        </div>
    );

    const saveSearchType = getItemSavedSearchType(facets);
    const isFollowableSearch = Boolean(saveSearchType);

    return (
        <div className={wrapperClasses}>
            <div className={styles['item-card']}>
                {narrow ? (
                    <div className={styles.flexbox}>
                        <h2 className={styles['item-title']}>{safeTitle}</h2>
                        <div className={styles.horizontal}>
                            <div className={styles['item-image']}>
                                <ImageWithFallback placeholder={imageUrlPlaceholder} src={imageUrl} />
                            </div>
                            <div className={styles.vertical}>
                                {itemStatusNode}
                                {itemDetailsNode}
                                {itemCTANode}
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className={styles['item-image']}>
                            <ImageWithFallback placeholder={imageUrlPlaceholder} src={imageUrl} />
                        </div>
                        <div className={styles.flexbox}>
                            <h2 className={styles['item-title']}>{safeTitle}</h2>
                            {itemStatusNode}
                            <div className={styles.horizontal}>
                                {itemDetailsNode}
                                {itemCTANode}
                            </div>
                        </div>
                    </>
                )}
            </div>

            <div className={styles['follow-search-cta']}>
                <div className={styles['print']}>
                    <div className={styles['title']}>
                        <FormattedMessage id="dont_miss_next" />
                    </div>
                    <div className={styles['sub-title']}>
                        {isFollowableSearch ? (
                            <FormattedMessage id="set_up_item_search_alert" values={{ token: saveSearchType }} />
                        ) : (
                            <FormattedMessage id="set_up_search_alert" values={{ sellerName }} />
                        )}
                    </div>
                </div>
                <div className={styles['button']}>
                    {isFollowableSearch ? (
                        <SaveSearchContainer
                            allSearchParams={allSearchParams}
                            primary
                            saveSearchParams={saveSearchParams}
                        />
                    ) : (
                        <FollowSellerContainer button sellerId={sellerId} />
                    )}
                </div>
            </div>
        </div>
    );
};

export default React.memo<Props>(SoldSnippet);
